import { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { AutoResizer } from 'react-base-table';
import { Retry } from '@components/common';
import humanizeValue from '@utils/overview/humanizeValue';
import {
  adContentDataSelector,
  adContentLoadingSelector,
} from '@store/overviewDashboard/kpiData/selectors';
import { HelpPopup } from '@components/overview-dashboard/common/help-popup';
import {
  AdContentTableWrapper,
  StyledBaseTable,
  BoldTextWrap,
  LightTextWrap,
  StartupTableWrapper,
  ContentWrapper,
  StyledPara,
} from './styled';

const formatData = ({ column, rowData }) => {
  const { metric } = rowData;
  const { dataKey } = column;
  const { unit } = metric;
  const formattedValue = humanizeValue(rowData[dataKey], metric, unit);

  return (
    <span>
      {rowData[dataKey] === null ? (
        <LightTextWrap>N/A</LightTextWrap>
      ) : (
        <>
          <BoldTextWrap>{formattedValue.humanizedNumber}</BoldTextWrap>
          {formattedValue.humanizedUnit}
        </>
      )}
    </span>
  );
};

const preRollColumns: any = [
  {
    title: 'Startup Metrics',
    key: 'metricName',
    dataKey: 'metricName',
    width: 0,
    align: 'left',
    flexGrow: 1,
  },
  {
    title: 'Overall',
    key: 'overAll',
    dataKey: 'overAll',
    dataGetter: formatData,
    width: 100,
    align: 'right',
  },
  {
    title: 'With PreRoll',
    key: 'WithPreRoll',
    dataKey: 'WithPreRoll',
    dataGetter: formatData,
    width: 120,
    align: 'right',
  },
  {
    title: 'Without PreRoll',
    key: 'WithoutPreRoll',
    dataKey: 'WithoutPreRoll',
    dataGetter: formatData,
    width: 140,
    align: 'right',
  },
];

const startupColumns: any = [
  {
    title: 'Playback Metrics',
    key: 'metricName',
    dataKey: 'metricName',
    width: 0,
    align: 'left',
    flexGrow: 1,
  },
  {
    title: 'Overall',
    key: 'overAll',
    dataKey: 'overAll',
    dataGetter: formatData,
    width: 100,
    align: 'right',
  },
  {
    title: 'With Ad',
    key: 'WithAds',
    dataKey: 'WithAds',
    dataGetter: formatData,
    width: 120,
    align: 'right',
  },
  {
    title: 'Without Ad',
    key: 'WithoutAds',
    dataKey: 'WithoutAds',
    dataGetter: formatData,
    width: 140,
    align: 'right',
  },
];

const renderHelpText = () => {
  const data: any = [];
  data.push(
    <ContentWrapper>
      <StyledPara>
        This table compares performance of streams which requested Ads and streams that did not.
      </StyledPara>
      <StyledPara>
        For start-up metrics, the comparison is between streams which requested at least one
        pre-roll Ad and streams which did not request any pre-roll Ad. This is because only the
        pre-roll Ad has an impact on start-up. For example, a session which requested a mid-roll Ad
        cannot be categorised as VSF or Abandonment.
      </StyledPara>
      <StyledPara>
        For playback metrics, the comparison is between streams which requested an Ad and streams
        which did not request any Ad. The percentage of traffic in each group is important to ensure
        that the comparison is insightful. Also, multi-dimensional filtering is available to compare
        different cohorts.
      </StyledPara>
    </ContentWrapper>,
  );
  return data;
};

function AdContentTable({ helpContext, retryHandler }) {
  const { loading, showError } = useSelector(adContentLoadingSelector);
  const data: any = useSelector(adContentDataSelector);
  
  const helpTextData = renderHelpText();
  return (
    <AdContentTableWrapper>
      <HelpPopup
        title="Ad-Content Opportunities Metrics"
        helpText={helpTextData}
        helpContext={helpContext}
      />
      <Retry
        isLoading={loading}
        showError={showError}
        isEmpty={isEmpty(data.preRoll)}
        mountChildAlways={false}
        fetchData={retryHandler}
      >
        <AutoResizer>
          {({ width, height }) => (
            <StyledBaseTable
              key={1}
              data={data.preRoll}
              columns={preRollColumns}
              headerHeight={30}
              rowHeight={30}
              minHeight={250}
              fixed={false}
              width={width}
              height={height}
            />
          )}
        </AutoResizer>
        <StartupTableWrapper>
          <AutoResizer>
            {({ width, height }) => (
              <StyledBaseTable
                key={2}
                data={data.ads}
                columns={startupColumns}
                headerHeight={30}
                rowHeight={30}
                minHeight={250}
                fixed={false}
                width={width}
                height={height}
              />
            )}
          </AutoResizer>
        </StartupTableWrapper>
      </Retry>
    </AdContentTableWrapper>
  );
}

export default AdContentTable;
